import { useSearchParams } from '@solidjs/router';
import { createContext, createEffect, createSignal, useContext } from 'solid-js';
import { isServer } from 'solid-js/web';
import type { Accessor, ParentProps } from 'solid-js';

const keys = ['campaign', 'source', 'medium', 'term', 'content'] as const;

type UTMKey = (typeof keys)[number];

type Params = Record<UTMKey, string | undefined>;

const storageKey = 'utm20241127';
const oneDayInMs = 1000 * 60 * 60 * 24;

const Ctx = createContext<{ utm: Accessor<Params> }>({ utm: () => ({}) as Params });

/**
 * Non-reactive source-param tracker with saving to localstorage
 */
export function UtmProvider(props: ParentProps) {
	const [search] = useSearchParams();
	const [utm, setUtm] = createSignal<Params>(getUTM(search).params);

	createEffect(() => {
		if (isServer) {
			return;
		}

		const { params, hasUTM } = getUTM(search);

		try {
			if (hasUTM) {
				setUtm(params);
				localStorage.setItem(storageKey, JSON.stringify({ ...params, created: Date.now() }));
			}
		} catch {
			// ignore
		}

		// localstorage is known to be finicky. guard and don't worry about fallback for now
		try {
			const storage = localStorage.getItem(storageKey);
			if (storage) {
				const { created, ...stored } = JSON.parse(storage) as Params & { created: number };
				const ttlMet = Date.now() > created + oneDayInMs;
				if (stored && !ttlMet) {
					setUtm(stored);
				} else if (ttlMet) {
					// Delete the stored source after 1 hour
					localStorage.removeItem(storageKey);
				}
			}
		} catch {
			// ignore
		}
	});

	return <Ctx.Provider value={{ utm }}>{props.children}</Ctx.Provider>;
}

export function useUtmParams() {
	const ctx = useContext(Ctx);
	return ctx.utm;
}

const defaultParams: Params = {
	campaign: undefined,
	source: undefined,
	medium: undefined,
	term: undefined,
	content: undefined,
};

function getUTM(input: Partial<Params>) {
	const params = { ...defaultParams };
	let hasUTM = false;
	for (const [key, val] of Object.entries(input)) {
		if (key.startsWith('utm_') && keys.find((utmKey) => utmKey === key.replace(/^utm_/, ''))) {
			hasUTM = true;
			params[key.replace(/^utm_/, '') as UTMKey] = Array.isArray(val) ? val[val.length - 1] : val;
		}
	}
	return { params, hasUTM };
}

type UTMParams = {
	utmCampaign?: string;
	utmSource?: string;
	utmMedium?: string;
	utmTerm?: string;
	utmContent?: string;
};

export function utmToQuery(params?: Partial<Params>) {
	return Object.entries(params ?? {}).reduce((memo, [key, val]) => {
		memo[`utm${key.charAt(0).toUpperCase()}${key.slice(1)}` as keyof UTMParams] = val;
		return memo;
	}, {} as UTMParams);
}
